import { parse } from 'papaparse';
import type { ZodType } from 'zod';
import { referencesImport } from '@r/api/generated/client.ts';
import type {
  ReferenceReferenceImportModel,
  ImportReferenceDtoReferenceImportModel
} from '@r/api/generated/client.schemas.ts';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
  dedupeErrors,
  normalizeEmpty,
  mapViolationsToErrorsWithIndexMap,
  resolveErrorValue
} from './import-utils.ts';
import { isOrvalError } from '@r/api/orval-error-helper.ts';

// Schémas d'entrée acceptés selon l'endpoint d'import visé
type bodySchemaType = ZodType<ReferenceReferenceImportModel>;

// Liste blanche des endpoints supportés pour cet import CSV
type urls = '/admin/references/import';

// Format d'une erreur utilisateur remontée par le parser (client ou serveur)
export interface ImportError {
  message: string;
  line?: number;
  field?: string;
  value?: string;
  error?: string;
  originalData?: Record<string, unknown>;
}

// Résultat global de l'opération d'import (succès/échec + messages)
export interface ImportAnalysis {
  totalLines: number;
  errorLines: number;
  importableLines: number;
  errorPercentage: number;
}

export interface ImportResult {
  success: boolean;
  message?: string;
  errors?: ImportError[];
  isPreview?: boolean;
  analysis?: ImportAnalysis;
}

const buildImportAnalysis = (errors: ImportError[], totalLines: number): ImportAnalysis => {
  const errorLineNumbers = new Set<number>();

  errors.forEach((error, index) => {
    if (typeof error.line === 'number') {
      errorLineNumbers.add(error.line);
      return;
    }

    errorLineNumbers.add(-(index + 1));
  });

  const errorLines = Math.min(errorLineNumbers.size, totalLines);
  const importableLines = Math.max(totalLines - errorLines, 0);
  const errorPercentage = totalLines > 0 ? Math.round((errorLines / totalLines) * 100) : 0;

  return {
    totalLines,
    errorLines,
    importableLines,
    errorPercentage
  };
};

const normalizeReferenceData = (
  referenceData: ReferenceReferenceImportModel
): ReferenceReferenceImportModel => {
  referenceData.pointValue = Number.parseInt(String(referenceData.pointValue), 10);

  const rawTags = (referenceData as unknown as { tags?: unknown }).tags;
  if (rawTags !== undefined) {
    referenceData.tags = (Array.isArray(rawTags) ? rawTags : String(rawTags).split(',')).flatMap(
      (tag) => {
        const normalizedTag = String(tag).trim();
        return normalizedTag ? [normalizedTag] : [];
      }
    );
  }

  return referenceData;
};

export function usePutFileParserWithErrors(bodySchema: bodySchemaType, url: urls) {
  const { t } = useTranslation();
  const FIRST_DATA_LINE_NUMBER = 2;

  // Mémoire des erreurs de la précédente tentative pour permettre un import forcé
  const [previousErrors, setPreviousErrors] = useState<ImportError[]>([]);

  // Convertit les chaînes vides en undefined pour faciliter la validation Zod (optionnels)
  const tranformFunction = (value: string | number) =>
    normalizeEmpty(value) as string | number | undefined;

  // Types d'API selon l'URL (corps de requête)
  type body = ImportReferenceDtoReferenceImportModel;

  // Lance le parsing + validation + envoi. Si forceImport=true, on exclut les lignes invalides
  const onParse = (files: File[], forceImport = false): Promise<ImportResult> => {
    return new Promise((resolve, reject) => {
      parse<ReferenceReferenceImportModel>(files[0], {
        // CSV courants Windows
        encoding: 'ISO-8859-1',
        // Entête présent en première ligne
        header: true,
        // Auto-détection du délimiteur (virgule ou point-virgule)
        delimiter: '',
        complete: (result) => {
          const data = result.data;

          if (result.errors.length) {
            // Erreurs de parsing "brutes" (mauvais CSV, colonnes décalées, etc.)
            reject(t('import.process.parsing.error'));
          }

          // Collecte des erreurs de validation locale (Zod) par ligne/champ
          const formatedErrors: ImportError[] = [];

          let referenceData: ReferenceReferenceImportModel;

          // Parcours des lignes pour appliquer les conversions spécifiques et valider localement
          data.forEach((rowData, index) => {
            let transformedData = { ...rowData };

            switch (url) {
              case '/admin/references/import':
                referenceData = normalizeReferenceData(
                  transformedData as ReferenceReferenceImportModel
                );
                transformedData = referenceData;
                console.info('Final transformedData for reference:', transformedData);
                break;
            }

            // Validation locale contre le schéma Zod, formatage d'erreurs par ligne/champ
            const result = bodySchema.safeParse(transformedData);
            if (!result.success) {
              const errors = result?.error?.format();
              for (const [key, value] of Object.entries(errors)) {
                if (typeof value === 'object' && value !== null && '_errors' in value)
                  for (const error of value._errors) {
                    formatedErrors.push({
                      message: t('file.parser.error.line', {
                        line: index + FIRST_DATA_LINE_NUMBER,
                        field: key,
                        error
                      }),
                      line: index + FIRST_DATA_LINE_NUMBER,
                      field: key,
                      value: resolveErrorValue(
                        key,
                        (rowData as unknown as Record<string, unknown>)[key],
                        t
                      ),
                      error: error,
                      originalData: rowData as unknown as Record<string, unknown>
                    });
                  }
              }
            }
          });

          // En première passe, on analyse systématiquement le fichier sans écrire en base:
          // - on isole les lignes valides localement
          // - on exécute un dry-run côté serveur pour récupérer d'éventuelles erreurs backend
          if (!forceImport) {
            const errorLineIndices = new Set(
              formatedErrors.map((error) =>
                typeof error.line === 'number'
                  ? Math.max(error.line - FIRST_DATA_LINE_NUMBER, 0)
                  : 0
              )
            );
            const indexMapValid = data.map((_, i) => i).filter((i) => !errorLineIndices.has(i));
            let validRows = data.filter((_, index) => !errorLineIndices.has(index));

            // Harmoniser la forme des données pour le serveur
            validRows = validRows.map((rowData) => {
              let transformedData = { ...rowData } as typeof rowData;

              switch (url) {
                case '/admin/references/import': {
                  transformedData = normalizeReferenceData(
                    transformedData as ReferenceReferenceImportModel
                  );
                  break;
                }
              }

              return transformedData as typeof rowData;
            });

            if (validRows.length === 0) {
              setPreviousErrors(formatedErrors);
              resolve({
                success: false,
                errors: formatedErrors,
                isPreview: true,
                analysis: buildImportAnalysis(formatedErrors, data.length)
              });
              return;
            }

            // Typage explicite pour satisfaire le type body en fonction de l'URL
            const bodyForDryRun: body = { data: validRows as ReferenceReferenceImportModel[] };
            referencesImport(bodyForDryRun, { headers: { 'dry-run': 'true' } })
              .then(() => {
                setPreviousErrors(formatedErrors);
                resolve({
                  success: false,
                  errors: formatedErrors,
                  isPreview: true,
                  analysis: buildImportAnalysis(formatedErrors, data.length)
                });
              })
              .catch((error) => {
                if (isOrvalError(error) && error.response?.status === 422) {
                  const apiErrors: ImportError[] = [];
                  const violations = (
                    error.response.data as {
                      violations?: Array<{ property: string; detail: string }>;
                    }
                  )?.violations;
                  if (violations) {
                    // Mapper les violations Orval vers ViolationLike (ajouter message depuis detail)
                    const violationsLike = violations.map((v) => ({ ...v, message: v.detail }));
                    apiErrors.push(
                      ...mapViolationsToErrorsWithIndexMap(
                        violationsLike,
                        indexMapValid,
                        data as unknown as Record<string, unknown>[],
                        t
                      )
                    );
                  }

                  // Fusion + déduplication des erreurs locales et serveur
                  const combinedErrors = dedupeErrors([...formatedErrors, ...apiErrors]);
                  setPreviousErrors(combinedErrors);

                  resolve({
                    success: false,
                    errors: combinedErrors,
                    isPreview: true,
                    analysis: buildImportAnalysis(combinedErrors, data.length)
                  });
                } else {
                  reject(t('import.process.error'));
                }
              });
            return;
          }
          // À partir d'ici: pas d'erreur locale bloquante OU import forcé
          let dataToSend = data;
          let indexMapToSend: number[] = data.map((_, i) => i);

          if (forceImport) {
            // En import confirmé: on retire les lignes connues en erreur (locales ou dry-run serveur)
            const errorsToExclude = dedupeErrors([...previousErrors, ...formatedErrors]);
            const errorLineIndices = new Set(
              errorsToExclude.map((error) =>
                typeof error.line === 'number'
                  ? Math.max(error.line - FIRST_DATA_LINE_NUMBER, 0)
                  : 0
              )
            );

            if (errorLineIndices.size > 0) {
              dataToSend = data.filter((_, index) => !errorLineIndices.has(index));
              indexMapToSend = indexMapToSend.filter(
                (originalIdx) => !errorLineIndices.has(originalIdx)
              );
            }
          }

          if (forceImport && dataToSend.length === 0) {
            // Rien à importer après exclusion -> on renvoie l'information utilisateur
            resolve({
              success: false,
              message: t('import.process.no.lines.imported', { totalLines: data.length }),
              errors: formatedErrors.length ? formatedErrors : previousErrors
            });
            return;
          }

          // Prépare les données finales pour l'API, avec normalisations par endpoint
          const transformedDataForApi = dataToSend.map((rowData) => {
            let transformedData = { ...rowData };

            switch (url) {
              case '/admin/references/import': {
                transformedData = normalizeReferenceData(
                  transformedData as ReferenceReferenceImportModel
                );
                break;
              }
            }

            return transformedData;
          });

          dataToSend = transformedDataForApi;

          console.info('dataToSend length:', dataToSend.length);
          console.info('About to call API:', url, 'with data:', dataToSend);
          const bodyToSend: body = { data: dataToSend as ReferenceReferenceImportModel[] };
          referencesImport(bodyToSend)
            .then((response: unknown) => {
              console.info('API call successful:', response);
              // Message de succès complet/partiel selon le nombre de lignes réellement importées
              const resp = response as { linesImported?: number } | undefined;
              const importedLines =
                typeof resp?.linesImported === 'number' ? resp.linesImported : dataToSend.length;
              const totalLines = data.length;
              let message = t('import.success');
              if (importedLines < totalLines) {
                message = t('import.success.partial', {
                  message: t('import.success'),
                  importedLines,
                  totalLines
                });
              }
              resolve({ success: true, message });
            })
            .catch((error) => {
              console.error('API call failed:', error);
              // Fallback succès si le serveur a bien accepté mais que la validation de réponse échoue
              if (
                (isOrvalError(error) &&
                  error.response?.status &&
                  error.response.status >= 200 &&
                  error.response.status < 300) ||
                !isOrvalError(error) /* ex: ZodError de parsing réponse */
              ) {
                resolve({ success: true, message: t('import.success') });
                return;
              }
              if (isOrvalError(error) && error.response?.status === 202) {
                // Traitement asynchrone accepté côté serveur (ex: en file)
                resolve({
                  success: true,
                  message:
                    forceImport && formatedErrors.length > 0
                      ? t('import.success.with.errors', {
                          message: t('import.success'),
                          importedLines: dataToSend.length,
                          totalLines: data.length
                        })
                      : t('import.success')
                });
                return;
              }

              if (isOrvalError(error) && error.response?.status === 422) {
                const apiErrors: ImportError[] = [];
                const serverErrorIndicesOnPost = new Set<number>();
                const violations = (
                  error.response.data as {
                    violations?: Array<{ property: string; detail: string }>;
                  }
                )?.violations;
                if (violations) {
                  // Mapper les violations Orval vers ViolationLike (ajouter message depuis detail)
                  const violationsLike = violations.map((v) => ({ ...v, message: v.detail }));
                  const mappedErrors = mapViolationsToErrorsWithIndexMap(
                    violationsLike,
                    indexMapToSend,
                    data as unknown as Record<string, unknown>[],
                    t
                  );
                  for (const err of mappedErrors) {
                    if (typeof err.line === 'number') {
                      const originalIndex = err.line - FIRST_DATA_LINE_NUMBER;
                      const postIdx = indexMapToSend.indexOf(originalIndex);
                      if (postIdx >= 0) serverErrorIndicesOnPost.add(postIdx);
                    }
                    apiErrors.push(err);
                  }
                }

                // Fusion + déduplication des erreurs locales et serveur
                const combinedErrors = dedupeErrors([...formatedErrors, ...apiErrors]);

                // Persiste pour un import forcé ultérieur (exclusion des lignes en erreur)
                setPreviousErrors(combinedErrors);

                if (forceImport) {
                  // Ré-essai automatique: on retire les lignes rejetées par le serveur et on renvoie
                  const reDataToSend = dataToSend.filter(
                    (_, idx) => !serverErrorIndicesOnPost.has(idx)
                  );

                  if (reDataToSend.length === 0) {
                    // Plus aucune ligne importable
                    resolve({
                      success: false,
                      message: t('import.process.no.lines.imported', { totalLines: data.length }),
                      errors: combinedErrors
                    });
                    return;
                  }

                  const reBodyToSend: body = {
                    data: reDataToSend as ReferenceReferenceImportModel[]
                  };
                  referencesImport(reBodyToSend)
                    .then((response2: unknown) => {
                      const resp2 = response2 as { linesImported?: number } | undefined;
                      const importedLines =
                        typeof resp2?.linesImported === 'number'
                          ? resp2.linesImported
                          : reDataToSend.length;
                      const totalLines = data.length;
                      let message = t('import.success');
                      if (importedLines < totalLines) {
                        message = t('import.success.partial', {
                          message: t('import.success'),
                          importedLines,
                          totalLines
                        });
                      }
                      resolve({ success: true, message });
                    })
                    .catch((err2) => {
                      if (
                        (isOrvalError(err2) &&
                          err2.response?.status &&
                          err2.response.status >= 200 &&
                          err2.response.status < 300) ||
                        !isOrvalError(err2)
                      ) {
                        resolve({ success: true, message: t('import.success') });
                        return;
                      }
                      // Deuxième tentative échouée -> on remonte la liste consolidée d'erreurs
                      resolve({ success: false, errors: combinedErrors });
                    });
                  return;
                }

                resolve({ success: false, errors: combinedErrors });
              } else {
                reject(t('import.process.error'));
              }
            });
        },
        skipEmptyLines: true,
        transform: tranformFunction
      });
    });
  };

  return { onParse };
}
